import useSessionNotice from '@/hooks/useSessionNotice';
import { useCallback } from 'react';

/**
 * The single entry point for dispatching a generation request — `POST /cvc`
 * (all four CVC shapes) and `POST /mss`.
 *
 * Same two responsibilities as `useUploadSlots`, for the same reasons: the
 * session guard, and one acknowledgement when the request fails. What differs
 * is what a failure costs. Every one of these call sites reads
 * `updateJobIds(jobIds)` on the line after the request, so a rejection used to
 * unwind the handler before any result row was created — the Generate button
 * did nothing at all, with no row, no FAILED badge and no message. That is
 * also the failure a schema or auth mismatch produces first when a new backend
 * is put behind this client, which is precisely when a silent no-op is most
 * expensive to diagnose.
 *
 * `null` is the failure signal rather than an empty array, so a caller cannot
 * confuse "the request failed" with "the request succeeded and returned no
 * jobs" — the latter is a real, different outcome (an interpolation run with
 * an empty group).
 *
 * NOTE for the caught error: it is logged, not handed to the global error sink
 * (`src/util/errorSink.ts`). The sink's scope is failures that would otherwise
 * be silent, and after this hook they are not — adding it would give the user
 * the modal AND a second, redacted console voice for one event.
 */
const useJobSubmit = () => {
  const { notifyNotConnected, notifyGenerateFailed } = useSessionNotice();

  return useCallback(
    async <T>(
      sessionId: string,
      submit: () => Promise<T>
    ): Promise<T | null> => {
      if (!sessionId) {
        notifyNotConnected();
        return null;
      }
      try {
        return await submit();
      } catch (error) {
        // console.error, not log: production builds strip everything else
        // (craco.config.js). This is the only place the reason survives — the
        // acknowledgement deliberately does not show it.
        console.error('failed to submit generation request', error);
        notifyGenerateFailed();
        return null;
      }
    },
    [notifyNotConnected, notifyGenerateFailed]
  );
};

export default useJobSubmit;
